import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BarChart3, Copy, Inbox, Plug, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Card, CardBody, CardHeader } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { StatusPill } from '@/components/status-dot';
import { Tabs } from '@/components/tabs';
import { SecretRevealDialog } from '@/components/secret-reveal';
import { FormFieldRows } from '@/components/form-field-rows';
import { SubmissionChart } from '@/components/submission-chart';
import { StatDelta } from '@/components/stat-delta';
import { DataList, DataListRow } from '@/components/data-list';
import { Menu } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  toDraft,
  useClientForm,
  useDeleteForm,
  useFieldCatalog,
  toPayloadFields,
  useFormAnalytics,
  useRegenerateFormSecret,
  useUpdateForm,
  type FieldDraft,
} from '@/lib/forms';
import { useFormError } from '@/lib/form-error';
import { formatDate, humanizeKey } from '@/lib/format';
import type { ClientFormData } from '@/types/api';

const STATUS_TONE = { active: 'success', paused: 'warning', archived: 'neutral' } as const;

export function ClientFormDetail() {
  const { formId } = useParams();
  const id = Number(formId);
  const form = useClientForm(Number.isFinite(id) ? id : undefined);
  const del = useDeleteForm();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();

  if (form.isLoading) return <SkeletonList rows={6} />;

  if (!form.data) {
    return (
      <EmptyState
        icon={Inbox}
        title="Form not found"
        description="It may have been deleted, or the link is wrong."
        action={
          <Link to="/sites">
            <Button variant="secondary">Back to websites</Button>
          </Link>
        }
      />
    );
  }

  const data = form.data;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Inbox}
        title={data.name}
        subtitle={[data.client_name, data.site_name].filter(Boolean).join(' · ') || undefined}
        badge={
          <StatusPill tone={STATUS_TONE[data.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
            {data.status}
          </StatusPill>
        }
        action={
          <div className="flex items-center gap-1.5">
            <Link to={`/forms?client_form_id=${data.id}`}>
              <Button variant="secondary">
                <Inbox className="size-4" /> {data.submissions_count ?? 0} submissions
              </Button>
            </Link>
            <Menu
              items={[
                {
                  key: 'delete',
                  label: 'Delete form',
                  icon: Trash2,
                  danger: true,
                  onSelect: async () => {
                    const ok = await confirm({
                      title: `Delete ${data.name}?`,
                      body: `The client's site stops being able to post to it, so anything submitted from that page is lost from the moment this form goes. Submissions already in the inbox stay. Set the status to paused instead if this is temporary.`,
                      confirmLabel: 'Delete form',
                      tone: 'danger',
                    });
                    if (ok) {
                      await del.mutateAsync(data.id);
                      toast.success(`${data.name} deleted`);
                      navigate(data.site_id ? `/sites/${data.site_id}` : '/sites');
                    }
                  },
                },
              ]}
            />
          </div>
        }
      />

      <Tabs
        paramName="tab"
        tabs={[
          { key: 'insights', label: 'Insights', icon: BarChart3, content: <InsightsTab form={data} /> },
          { key: 'fields', label: 'Fields', icon: Sparkles, content: <FieldsTab form={data} /> },
          { key: 'setup', label: 'Setup', icon: Plug, content: <SetupTab form={data} /> },
        ]}
      />
    </div>
  );
}

function FieldsTab({ form }: { form: ClientFormData }) {
  const catalog = useFieldCatalog();
  const update = useUpdateForm(form.id);
  const { error, wrap } = useFormError();
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const [rows, setRows] = useState<FieldDraft[]>(form.fields.map(toDraft));

  useEffect(() => {
    setRows(form.fields.map(toDraft));
  }, [form.fields]);

  /**
   * Build a row for a discovered key, applying whatever the server detected
   * from the values it has actually received. Roles already taken by a row in
   * the current draft are skipped, so adding several at once cannot claim the
   * same role twice.
   */
  function draftFor(key: string, claimed: Set<string>): FieldDraft {
    const guess = form.suggestions[key];
    const role = guess?.role && !claimed.has(guess.role) ? guess.role : '';

    return {
      key,
      label: humanizeKey(key),
      type: guess?.type ?? 'text',
      role,
      required: false,
      // The row shows only the name (as its title), the message (as its
      // preview) and whatever is flagged here — so a contact channel has to be
      // flagged by default or a fresh form's rows carry no way to reach anyone.
      include_in_summary: role === 'email' || role === 'phone',
      options: [],
      discovered: true,
      detectedType: Boolean(guess?.type),
      detectedRole: Boolean(role),
    };
  }

  function adopt(keys: string[]) {
    setRows((current) => {
      const claimed = new Set(current.map((r) => r.role).filter(Boolean) as string[]);
      const added: FieldDraft[] = [];

      for (const key of keys) {
        const draft = draftFor(key, claimed);
        if (draft.role) claimed.add(draft.role);
        added.push(draft);
      }

      return [...current, ...added];
    });
  }

  async function save() {
    setPending(true);
    let saved: ClientFormData | undefined;
    const ok = await wrap(async () => {
      saved = await update.mutateAsync({ fields: toPayloadFields(rows) });
    });
    setPending(false);

    if (ok) {
      // Saving a map also re-applies it to submissions already stored, so say
      // so — otherwise it looks like nothing happened to the inbox.
      const count = saved?.submissions_count ?? 0;
      toast.success(
        'Field map saved',
        count > 0
          ? `${count} existing submission${count === 1 ? '' : 's'} re-checked against it.`
          : undefined,
      );
    }
  }

  // Filter against the working draft, not the server's list, so a chip
  // disappears the moment you add it rather than on the next save.
  const mapped = new Set(rows.map((r) => r.key));
  const pendingKeys = form.unmapped_keys.filter((k) => !mapped.has(k));

  return (
    <div className="space-y-4">
      {pendingKeys.length > 0 ? (
        <Card className="border-[var(--warning)]/40">
          <CardBody className="space-y-3">
            <div>
              <p className="text-sm font-medium">
                {pendingKeys.length} field{pendingKeys.length === 1 ? '' : 's'} arrived that you
                have not mapped
              </p>
              <p className="mt-0.5 text-sm text-[var(--color-foreground-muted)]">
                Their values are stored in full either way. Mapping adds a label and a type, and
                we have guessed both from what has actually come in.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => adopt(pendingKeys)}>
                <Sparkles className="size-4" /> Add all {pendingKeys.length}
              </Button>
              <span className="text-xs text-[var(--color-foreground-subtle)]">or one at a time:</span>
              {pendingKeys.map((key) => (
                <Button key={key} variant="secondary" size="sm" onClick={() => adopt([key])}>
                  {key}
                </Button>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : null}

      <FormFieldRows
        rows={rows}
        onChange={setRows}
        catalog={catalog.data}
        samples={form.field_samples}
      />

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <div className="flex items-center justify-end gap-3">
        <p className="text-xs text-[var(--color-foreground-subtle)]">
          Nothing is lost by leaving a field unmapped.
        </p>
        <Button onClick={save} disabled={pending}>
          {pending ? 'Saving…' : 'Save field map'}
        </Button>
      </div>
    </div>
  );
}

function SetupTab({ form }: { form: ClientFormData }) {
  const update = useUpdateForm(form.id);
  const regenerate = useRegenerateFormSecret(form.id);
  const confirm = useConfirm();
  const toast = useToast();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState(form.honeypot_key ?? '');
  const [status, setStatus] = useState(form.status);

  async function rotate() {
    if (form.has_secret) {
      const ok = await confirm({
        title: 'Generate a new secret?',
        body: "The current one stops working immediately, and the client's site will stop delivering until it is updated.",
        confirmLabel: 'Generate new',
        tone: 'danger',
      });
      if (!ok) return;
    }
    const { secret } = await regenerate.mutateAsync();
    setRevealed(secret);
  }

  const sample = buildSample(form);

  return (
    <div className="max-w-3xl space-y-4">
      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Ingest secret</h3>
          {form.has_secret ? (
            <StatusPill tone="success">Issued</StatusPill>
          ) : (
            <StatusPill tone="warning">Not set</StatusPill>
          )}
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-[var(--color-foreground-muted)]">
            Sent as the <code className="font-mono text-xs">X-Form-Secret</code> header. This form
            will not accept anything until a secret exists.
          </p>
          <Button variant="secondary" onClick={rotate} disabled={regenerate.isPending}>
            <RefreshCw className="size-4" />
            {regenerate.isPending ? 'Generating…' : form.has_secret ? 'Generate new' : 'Generate secret'}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Send it here</h3>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-[var(--color-foreground-muted)]">
            Give this to whoever maintains the site. The body below is built from the field map, so
            it always matches what this form expects.
          </p>
          <Snippet body={sample} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Settings</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">Status</span>
            <Select
              value={status}
              onChange={async (e) => {
                setStatus(e.target.value);
                await update.mutateAsync({ status: e.target.value });
                toast.success('Form updated');
              }}
              className="max-w-xs"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </Select>
            <span className="block text-xs text-[var(--color-foreground-muted)]">
              A paused form rejects submissions with a 403. Pausing the site does the same.
            </span>
          </label>

          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">Honeypot field</span>
            <div className="flex gap-2">
              <Input
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                placeholder="company"
                className="max-w-xs font-mono"
              />
              <Button
                variant="secondary"
                onClick={async () => {
                  await update.mutateAsync({ honeypot_key: honeypot || null });
                  toast.success('Honeypot updated');
                }}
              >
                Save
              </Button>
            </div>
            <span className="block text-xs text-[var(--color-foreground-muted)]">
              The name of a hidden input on their form that no human fills in. If it arrives with a
              value, the submission is dropped silently.
            </span>
          </label>
        </CardBody>
      </Card>

      {revealed ? (
        <SecretRevealDialog
          open
          onClose={() => setRevealed(null)}
          title="New form secret"
          label="Form secret"
          secret={revealed}
          description={`Paste this into the site that hosts "${form.name}".`}
        />
      ) : null}
    </div>
  );
}

function Snippet({ body }: { body: string }) {
  const toast = useToast();

  return (
    <div className="relative">
      <pre className="max-h-80 overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs leading-relaxed">
        {body}
      </pre>
      <Button
        variant="secondary"
        size="sm"
        className="absolute right-2 top-2"
        onClick={() => {
          void navigator.clipboard.writeText(body);
          toast.success('Snippet copied');
        }}
      >
        <Copy className="size-3.5" /> Copy
      </Button>
    </div>
  );
}

/** Built from the live field map so the example never drifts from reality. */
function buildSample(form: ClientFormData): string {
  const sampleFor = (type: string): string => {
    switch (type) {
      case 'email':
        return '"jane@example.com"';
      case 'phone':
        return '"(509) 555-1234"';
      case 'number':
        return '5000';
      case 'boolean':
        return 'true';
      case 'date':
        return '"2026-03-04"';
      case 'multiselect':
        return '["Sales", "Support"]';
      case 'url':
      case 'file':
        return '"https://example.com/uploads/file.pdf"';
      case 'json':
        return '{ "nested": "anything" }';
      default:
        return '"…"';
    }
  };

  const lines = form.fields.length
    ? form.fields.map((f) => `  "${f.key}": ${sampleFor(f.type)}`)
    : ['  "your-field": "…"'];

  return [
    `POST ${form.endpoint_url}`,
    'Content-Type: application/json',
    'X-Form-Secret: <the secret you generated above>',
    '',
    '{',
    lines.join(',\n') + ',',
    '  "submitted_at": "2026-03-04T18:04:00Z"',
    '}',
    '',
    '→ 200 {"ok":true,"submission_id":123,"warnings":[]}',
    '',
    '# Extra keys are fine — they are stored and shown to you as unmapped.',
    '# A bad value is stored too, flagged rather than rejected, so a real',
    '# enquiry is never lost to a schema mismatch.',
  ].join('\n');
}

function InsightsTab({ form }: { form: ClientFormData }) {
  const [granularity, setGranularity] = useState('month');
  const analytics = useFormAnalytics(form.id, { granularity });
  const data = analytics.data;

  if (analytics.isLoading) return <SkeletonList rows={3} />;
  if (!data) return null;

  if (data.all_time_count === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="No submissions yet"
        description="Once the site starts posting to this endpoint, volume over time shows up here."
      />
    );
  }

  // The accessible table view the chart stands in for — most recent first.
  const recent = [...data.buckets].reverse().slice(0, 6);

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[var(--color-foreground-muted)]">
          {data.all_time_count} submissions all time
          {data.first_submission_at ? `, since ${formatDate(data.first_submission_at)}` : ''}.
        </p>
        <Select
          value={granularity}
          onChange={(e) => setGranularity(e.target.value)}
          className="max-w-[150px]"
          aria-label="Bucket size"
        >
          <option value="week">By week</option>
          <option value="month">By month</option>
          <option value="year">By year</option>
        </Select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <StatDelta title={data.current_bucket.label} comparison={data.current_bucket} />
        <StatDelta title={data.period_total.label} comparison={data.period_total} />
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Submissions over time</h3>
        </CardHeader>
        <CardBody>
          <SubmissionChart buckets={data.buckets} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Recent periods</h3>
        </CardHeader>
        <CardBody className="p-0">
          <DataList className="rounded-none border-0 shadow-none">
            {recent.map((bucket) => (
              <DataListRow key={bucket.starts_at} interactive={false}>
                <span className="flex-1 text-sm">{bucket.label}</span>
                <span className="text-sm tabular-nums text-[var(--color-foreground-muted)]">
                  {bucket.count}
                </span>
              </DataListRow>
            ))}
          </DataList>
        </CardBody>
      </Card>

      <p className="text-xs text-[var(--color-foreground-subtle)]">
        Periods run on {data.timezone} time.
      </p>
    </div>
  );
}
